import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, UserCheck, Activity, FileText, TrendingUp, RefreshCw, LayoutDashboard, Sparkles, BarChart3, PieChart as PieChartIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isCustomerAdmin, isAdmin } from '../utils/rbac';
import { getCustomerDashboard } from '../api/dashboard';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import StatCard from '../components/charts/StatCard';
import ProgressRing from '../components/charts/ProgressRing';
import TrendChart from '../components/charts/TrendChart';
import PieChart from '../components/charts/PieChart';
import toast from 'react-hot-toast';

const CustomerAdminDashboardEnhanced = ({ customerId = null, inAdminMode = false }) => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!inAdminMode && (!user || (!isCustomerAdmin(user) && !isAdmin(user)))) {
      setError('Access denied');
      setLoading(false);
      return;
    }
    fetchDashboardData();
  }, [user, customerId, inAdminMode]);

  const fetchDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await getCustomerDashboard(customerId);
      setDashboardData(response.data);
      setLastUpdated(new Date());
      
      if (response.data?._migration_pending) {
        setError('Database migration pending. Please run the migration script.');
      }
    } catch (err) {
      console.error('Failed to fetch customer dashboard data:', err);
      if (err.response?.status !== 401) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">{error}</div>
          <button 
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const data = dashboardData;
  if (!data) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-gray-600">No customer data available</div>
      </div>
    );
  }

  // Calculate completion rate
  const completionRate = data.total_assessments > 0 
    ? Math.round((data.completed_assessments / data.total_assessments) * 100) 
    : 0;

  // Prepare assessment type distribution data
  const assessmentTypeData = [
    { name: 'BIG FIVE', value: data.bigfive_completed || 0 },
    { name: 'Advanced', value: data.advanced_completed || 0 },
    { name: 'Productivity', value: (data.completed_assessments || 0) - (data.bigfive_completed || 0) - (data.advanced_completed || 0) }
  ].filter(item => item.value > 0);

  // User growth data from backend
  const userGrowthData = data.user_growth_data || [];

  return (
    <div>
      {/* Header */}
      <motion.div 
        className="mb-8 flex items-center justify-between"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <LayoutDashboard className="w-6 h-6 mr-2 text-cobalt-600" />
          Customer Dashboard
        </h1>
        <div className="flex items-center space-x-2">
          {lastUpdated && (
            <span className="text-xs text-gray-500">
              Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs bg-cobalt-50 text-cobalt-600 rounded-lg hover:bg-cobalt-100 transition-all disabled:opacity-50 font-medium"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </motion.div>

      {/* Enhanced KPI Cards with Trends */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatCard
            icon={Users}
            label="Total Users"
            value={data.total_users || 0}
            trend={data.trends?.users}
            color="blue"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <StatCard
            icon={UserCheck}
            label="HR Managers"
            value={data.hr_count || 0}
            trend={data.trends?.hrs}
            color="green"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <StatCard
            icon={Activity}
            label="Total Assessments"
            value={data.total_assessments || 0}
            trend={data.trends?.assessments}
            color="purple"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <StatCard
            icon={FileText}
            label="Completed"
            value={data.completed_assessments || 0}
            trend={data.trends?.completion}
            color="orange"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <StatCard
            icon={Activity}
            label="Completion Rate"
            value={`${completionRate}%`}
            color="green"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <StatCard
            icon={Users}
            label="Employees"
            value={data.employee_count || 0}
            color="blue"
          />
        </motion.div>
      </div>

      {/* Charts Row 1: Completion Rate & Assessment Distribution */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Assessment Analytics</h2>
        <p className="text-sm text-gray-500">Completion progress and type distribution</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Completion Rate Progress Ring */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7 }}>
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 rounded-lg bg-gradient-to-br from-green-50 to-emerald-100">
                  <Activity className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">Assessment Completion Rate</h3>
              </div>
              <div className="flex flex-col items-center justify-center py-4">
                <ProgressRing 
                  percentage={completionRate} 
                  size={160} 
                  strokeWidth={12}
                  color="#10b981"
                />
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    {data.completed_assessments} of {data.total_assessments} assessments completed
                  </p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{data.bigfive_completed || 0}</p>
                  <p className="text-xs text-gray-600 mt-1">BIG FIVE</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{data.advanced_completed || 0}</p>
                  <p className="text-xs text-gray-600 mt-1">Advanced</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {(data.completed_assessments || 0) - (data.bigfive_completed || 0) - (data.advanced_completed || 0)}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Productivity</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Assessment Type Distribution */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 }}>
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-50 to-indigo-100">
                  <PieChartIcon className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">Assessment Type Distribution</h3>
              </div>
              {assessmentTypeData.length > 0 ? (
                <PieChart 
                  data={assessmentTypeData}
                  colors={['#3b82f6', '#8b5cf6', '#10b981']}
                  height={180}
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No completed assessments yet
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Row 2: User Growth & Activity Summary */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Growth & Activity Insights</h2>
        <p className="text-sm text-gray-500">User growth trends and activity summary</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* User Growth Trend */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}>
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-cyan-100">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">User Growth Trend</h3>
              </div>
              {userGrowthData.length > 0 ? (
                <TrendChart
                  data={userGrowthData}
                  dataKeys={[{ dataKey: 'users', name: 'Total Users' }]}
                  colors={['#3b82f6']}
                  type="area"
                  height={180}
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No growth data available
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Activity Summary */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}>
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 rounded-lg bg-gradient-to-br from-orange-50 to-amber-100">
                  <BarChart3 className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">Activity Summary</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Employees</p>
                    <p className="text-2xl font-bold text-blue-600">{data.employee_count || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-600" />
                </div>

                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Completion Rate</p>
                    <p className="text-2xl font-bold text-green-600">{completionRate}%</p>
                  </div>
                  <Activity className="w-8 h-8 text-green-600" />
                </div>

                {data.last_assessment_date && (
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Last Assessment</p>
                      <p className="text-lg font-bold text-purple-600">
                        {new Date(data.last_assessment_date).toLocaleDateString()}
                      </p>
                    </div>
                    <FileText className="w-8 h-8 text-purple-600" />
                  </div>
                )}

                <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    <p className="text-sm font-medium text-gray-700">Quick Insight</p>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {completionRate >= 80 
                      ? 'Excellent engagement! Your team is actively completing assessments.'
                      : completionRate >= 50
                      ? 'Good progress. Consider sending reminders to boost completion rates.'
                      : 'Low completion rate. Reach out to employees to encourage participation.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}>
        <Card className="border-0 shadow-lg">
          <CardContent className="p-5">
            <div className="flex items-center space-x-2 mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-gray-50 to-slate-100">
                <Activity className="w-5 h-5 text-gray-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900">Quick Actions</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <ActionButton
                title="HR Dashboard"
                description="View HR management tools"
                href="/hr/dashboard"
                icon="👔"
              />
              <ActionButton
                title="View Reports"
                description="Access employee reports"
                href="/reports"
                icon="📊"
              />
              <ActionButton
                title="User Management"
                description="Manage employees and HRs"
                href="/user-management"
                icon="👥"
              />
              <ActionButton
                title="Settings"
                description="Configure customer settings"
                href="/settings"
                icon="⚙️"
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

const ActionButton = ({ title, description, href, icon }) => {
  const handleClick = () => {
    window.location.href = href;
  };

  return (
    <button
      onClick={handleClick}
      className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all duration-200 text-left"
    >
      <div className="flex items-center mb-2">
        <span className="text-xl mr-2">{icon}</span>
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>
      <p className="text-sm text-gray-600">{description}</p>
    </button>
  );
};

export default CustomerAdminDashboardEnhanced;
